import React, { useState, useEffect } from 'react';
import Loading from '../../Common/Loading';
import { useNavigate } from "react-router-dom";
import popimg from "../../Common/Images/ImagesForAlert/popup_bg1.png";
import popbg from "../../Common/Images/ImagesForAlert/popup_bg2.png";
import popclose from "../../Common/Images/ImagesForAlert/popup_close_n.png";
import EL_SetLoginRoles from './EL_SetLoginRoles';
import modifyImgN from '../../Images/modify_n.gif';
import modifyImgO from '../../Images/modify_o.gif';
import closeImgN from '../../Images/close_n.gif';
import closeImgO from '../../Images/close_o.gif';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

// Pagination is disabled — all matching rows are loaded in a single request.
// A large page_size is sent so the existing backend pagination contract
// (page / page_size) doesn't need to change on the server side.
const ALL_ROWS_PAGE_SIZE = 100000;

const EL_ManageRoles: React.FC = () => {

    const [countries, setCountries] = useState<string[]>([]);
    const [schools, setSchools] = useState<string[]>([]);
    const [offices, setOffices] = useState<string[]>([]);

    const [selectedCountry, setSelectedCountry] = useState('-- Select --');
    const [selectedSchool, setSelectedSchool] = useState('-- Select --');
    const [selectedOffice, setSelectedOffice] = useState('-- Select --');
    const [total, setTotal] = useState(0);

    // Data States
    const [rolesData, setRolesData] = useState<any[]>([]);
    const [selectedRow, setSelectedRow] = useState<any | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [errorMsg, setErrorMsg] = useState<string>('');

    // Modal State
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

    const navigate = useNavigate();

    // --- Effects ---

    // Mount: page setup + load initial country dropdown options only.
    useEffect(() => {
        document.body.style.margin = '0px';
        document.body.style.fontFamily = 'Arial, sans-serif';
        document.body.style.backgroundColor = "azure";
        fetchOptions('country');
    }, []);

    // Country changed: load dependent school options.
    useEffect(() => {
        if (selectedCountry !== '-- Select --') {
            fetchOptions('school', `&country=${selectedCountry}`);
        } else {
            setSchools([]);
            setSelectedSchool('-- Select --');
        }
    }, [selectedCountry]);

    // School changed: load dependent office options.
    useEffect(() => {
        if (selectedSchool !== '-- Select --') {
            fetchOptions('office', `&school=${selectedSchool}`);
        } else {
            setOffices([]);
            setSelectedOffice('-- Select --');
        }
    }, [selectedSchool]);

    // Single source of truth for grid data.
    // Fires once on mount, and once whenever a filter changes.
    // No `page` dependency anymore since pagination has been removed —
    // all matching rows are loaded in one response.
    useEffect(() => {
        fetchGridData();
    }, [selectedCountry, selectedSchool, selectedOffice]);

    // --- API Calls ---
    const fetchOptions = async (type: string, query: string = '') => {
        try {
            const res = await fetch(`${API_BASE_URL}api/roles/filters?type=${type}${query}`);
            const json = await res.json();
            if (type === 'country') setCountries(json.options || []);
            if (type === 'school') setSchools(json.options || []);
            if (type === 'office') setOffices(json.options || []);
        } catch (err) {
            console.error(`Error loading ${type}`, err);
        }
    };

    const fetchGridData = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/roles/list`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    country: selectedCountry,
                    school_code: selectedSchool,
                    office_code: selectedOffice,
                    page: 1,
                    page_size: ALL_ROWS_PAGE_SIZE
                })
            });

            const json = await res.json();

            setRolesData(json.roles || []);
            setTotal(json.total || 0);

        } catch {
            setErrorMsg("Error loading login roles.");
        } finally {
            setLoading(false);
        }
    };

    // --- Action Handlers ---
    const handleModify = () => {
        if (!selectedRow) {
            alert("Please select a row first.");
            return;
        }
        setIsModalOpen(true);
    };

    const handleModalClose = (shouldRefresh: boolean = false) => {
        setIsModalOpen(false);
        if (shouldRefresh) {
            fetchGridData(); // Refresh the parent table if data was saved
        }
    };

    const handleClose = () => {
        navigate("/SysAdmin");
    };

    // Hover State for image buttons
    const [modHover, setModHover] = useState(false);
    const [closeHover, setCloseHover] = useState(false);

    // --- Giant list of columns derived from the VB code ---
    const columns = [
        { label: 'School Code', field: 'SchoolCode1' },
        { label: 'Login', field: 'UserLogin1' },
        { label: 'Category Code', field: 'CategoryCode' },
        { label: 'Category Name', field: 'CategoryName' },
        { label: 'Register New Family', field: 'RegisterNewFamily' },
        { label: 'List Families', field: 'ListFamilies' },
        { label: 'Add Points', field: 'AddPoints' },
        { label: 'List Subscriptions', field: 'ListSubscriptions' },
        { label: 'Data from Payment Website', field: 'ListOfDataCapturedFromPaymentWebsite' },
        // { label: 'Usage Analysis', field: 'UsageAnalysis' },
        { label: 'New Sales', field: 'NewSale' },
        { label: 'Customer Support Entry', field: 'EntryCustomerSupport' },
        { label: 'Customer Support View', field: 'ViewCustomerSupport' },
        // { label: 'Modify Sale', field: 'ModifySale' },
        // CHANGED FROM MODIFY SALE TO SALE 
        { label: 'Sale', field: 'ModifySale' },
        { label: 'Packages for Dispatch', field: 'PackagesForDispatch' },
        { label: 'Approve Delivery Dockets', field: 'ApproveDeliveryDockets' },
        { label: 'Payments Received', field: 'PaymentsReceived' },
        { label: 'Finance Sale', field: 'FinanceSale' },
        { label: 'Invoice List', field: 'InvoiceList' },
        { label: 'Installation List', field: 'InstallationList' },
        { label: 'Commission Payments', field: 'CommissionPaymentDetails' },
        { label: 'Order Packages', field: 'OrderPackages' },
        { label: 'Lead Collection', field: 'LeadCollection' },
        { label: 'Telemarketing', field: 'Telemarketing' },
        { label: 'Distributor Orders', field: 'DistributorOrders' },
        { label: 'Country Manager', field: 'CountryManager' },
        { label: 'Add Work force', field: 'AddWorkForce' },
        { label: 'Register Office', field: 'RegisterOffice' },
        { label: 'Search Engine', field: 'SearchEngine' },
        { label: 'Modify Dongle Status', field: 'ModifyDongleStatus' },
        { label: 'Dongle WorkFlow Report', field: 'DongleWorkFlowReport' },
        { label: 'Crelogix', field: 'Crelogix' },
        { label: 'Sales Report', field: 'SalesReport' },
        { label: 'DNC Search', field: 'DNCSearch' },
        { label: 'Add DNC', field: 'AddDNC' },
        { label: 'Strand/Substrand/Qns', field: 'StrandSubstrandQns' },
        { label: 'Voucher Codes', field: 'VoucherCodes' },
        { label: 'Maths Doctor- Maths Wiz Link', field: 'MathsDoctor' },
        { label: 'View Questions', field: 'ViewQuestions' },
        { label: 'View Exceptions', field: 'ViewExceptions' },
        { label: 'Post Question Category', field: 'PostQuestionCategory' },
        { label: 'View Lesson', field: 'ViewLesson' },
        { label: 'View Games', field: 'ViewGames' },
        { label: 'Default WLP running out - Maths', field: 'WLPRunningOutMaths' },
        { label: 'Default WLP running out - English', field: 'WLPRunningOutEnglish' },
        { label: 'MathsDoctor WLP running out', field: 'WLPRunningOutMD' },
        { label: 'ResetMD', field: 'ResetMD' },
        { label: 'Edit Loginnames', field: 'EditLogin_names' },
        { label: 'Add Lessons To WLP', field: 'AddLessonsToWLP' }
    ];

    const styles = {
        wrapper: { width: '98%', margin: '0 auto', fontFamily: 'Arial, sans-serif' },
        filterTable: { width: '100%', marginBottom: '10px' },
        title: { color: 'Blue', fontSize: 'large', fontWeight: 'bold', textAlign: 'center' as const, paddingBottom: '15px' },
        dropdown: { padding: '2px', marginLeft: '5px' },

        gridContainer: { width: '100%', overflowX: 'auto' as const, overflowY: 'auto' as const, height: '70vh', border: '1px solid #80a0ff' },
        gridTable: { borderCollapse: 'collapse' as const, whiteSpace: 'nowrap' as const, fontSize: '14px', border: '1px solid #80a0ff' },
        gridTh: { backgroundColor: '#c7ddfd', fontWeight: "normal", color: '#2859ac', padding: '10px 15px', border: '1px solid #a3befe', position: 'sticky' as const, top: 0, zIndex: 10, textWrap: 'nowrap' as const },
        gridTd: { padding: '8px 15px', borderLeft: '1px solid #a3befe', borderRight: '1px solid #a3befe', cursor: 'pointer', textAlign: 'center' as const },

        selectedRow: { backgroundColor: '#FFA500', color: 'white' },
        altRow: { backgroundColor: '#F4FAFF' },
        defaultRow: { backgroundColor: 'white' }
    };

    return (
        <>
            <div
                style={{
                    backgroundImage: `url(${popimg}), url(${popbg})`,
                    backgroundRepeat: "no-repeat, no-repeat",
                    backgroundPosition: "left center, right center",
                    backgroundSize: "30% 100%, 70% 100%",
                    height: "16px",
                    color: "white",
                    padding: "7px 0px",
                    fontWeight: "bold",
                    fontSize: "12pt",
                    fontFamily: "arial",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <span style={{ marginLeft: "10px" }}>Manage Login Roles</span>
                <img
                    src={popclose}
                    alt="close"
                    height="30px"
                    style={{ cursor: "pointer" }}
                    onClick={handleClose}
                />
            </div>

            <div style={styles.wrapper}>
                {loading && <Loading />}
                {errorMsg && <div style={{ color: 'red', fontWeight: 'bold', padding: '10px 0' }}>{errorMsg}</div>}

                {/* Render the specific Roles modal if open */}
                {isModalOpen && selectedRow && (
                    <EL_SetLoginRoles
                        schoolCode={selectedRow.SchoolCode1}
                        login={selectedRow.UserLogin1}
                        onClose={handleModalClose}
                    />
                )}

                {/* Filter Section */}
                <table style={styles.filterTable} cellPadding="15" cellSpacing="0">
                    <tbody>
                        <tr>
                            <td colSpan={4} style={styles.title}>Manage Login Roles</td>
                        </tr>
                        <tr>
                            <td style={{ width: '200px' }}>
                                Country:
                                <select style={{ ...styles.dropdown, width: '120px' }} value={selectedCountry} onChange={e => setSelectedCountry(e.target.value)}>
                                    <option value="-- Select --">-- Select --</option>
                                    {countries.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </td>
                            <td style={{ width: '370px' }}>
                                SchoolCode:
                                <select style={{ ...styles.dropdown, width: '250px' }} value={selectedSchool} onChange={e => setSelectedSchool(e.target.value)}>
                                    <option value="-- Select --">-- Select --</option>
                                    {schools.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </td>
                            <td>
                                Office Code:
                                <select style={{ ...styles.dropdown, width: '200px' }} value={selectedOffice} onChange={e => setSelectedOffice(e.target.value)}>
                                    <option value="-- Select --">-- Select --</option>
                                    {offices.map(o => <option key={o} value={o}>{o}</option>)}
                                </select>
                            </td>
                            <td></td>
                        </tr>
                    </tbody>
                </table>

                     <div
                        style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 20,
                            backgroundColor: "#c7ddfd",
                            color: "#003399",
                            textAlign: "center",
                            fontWeight: "bold",
                            fontSize: "15px",
                            height: "40px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "1px solid #a3befe",
                            textDecoration:"none"
                        }}
                    >
                        <u style={{textDecoration:"none"}}>Login Roles</u>
                    </div>

                {/* Giant Scrollable Data Grid */}
                <div style={styles.gridContainer}>
               
                    <table style={styles.gridTable}>
                        <thead>
                            {/* Column Headers — sticky just below the "Login Roles" bar */}
                            <tr>
                                <th style={styles.gridTh}>#</th>

                                {columns.map(col => (
                                    <th
                                        key={col.field}
                                        style={styles.gridTh}
                                        dangerouslySetInnerHTML={{ __html: col.label }}
                                    />
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rolesData.map((row, idx) => {
                                const key = `${row.SchoolCode1}_${row.UserLogin1}`;
                                const isSelected = selectedRow && selectedRow.SchoolCode1 === row.SchoolCode1 && selectedRow.UserLogin1 === row.UserLogin1;

                                return (
                                    <tr
                                        key={key}
                                        onClick={() => setSelectedRow(row)}
                                        style={isSelected ? styles.selectedRow : (idx % 2 === 0 ? styles.defaultRow : styles.altRow)}
                                    >
                                        <td style={styles.gridTd}>
                                            {idx + 1}
                                        </td>
                                        {columns.map(col => {
                                            let val = row[col.field];
                                            if (val === 'Y') val = 'Y';
                                            return (
                                                <td key={col.field} style={{ ...styles.gridTd, textAlign: ['SchoolCode1', 'UserLogin1', 'CategoryCode', 'CategoryName'].includes(col.field) ? 'left' : 'center' }}>
                                                    {val || ''}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                            {rolesData.length === 0 && !loading && (
                                <tr>
                                    <td colSpan={columns.length + 1} style={{ padding: '20px', textAlign: 'center' }}>No roles found for selected criteria.</td>
                                </tr>
                            )}


                        </tbody>

                    </table>

                </div>
                {/* Pagination UI kept for visual/layout parity but disabled —
                    all rows are loaded in a single request now, so paging
                    controls no longer perform any action. */}
                <div style={{ textAlign: "center", backgroundColor: "#c7ddfd", border: "1px solid #80a0ff", width: "100%", padding: "7px 0" }}>
                    <button disabled style={{ background: "none", border: "none", color: "gray", fontWeight: "bold" }}>{"<<"}</button>

                    <button disabled style={{ background: "none", border: "none", color: "gray", fontWeight: "bold" }}>{"<"}</button>

                    <span style={{ margin: "0 10px", color: '#2859ac', fontWeight: 'bold', fontSize: '14px' }}>
                        {1} of {1}
                    </span>

                    <button disabled style={{ background: "none", border: "none", color: "gray", fontWeight: "bold" }}>{">"}</button>
                    <button disabled style={{ background: "none", border: "none", color: "gray", fontWeight: "bold" }}>{">>"}</button>
                </div>

                {/* Footer Buttons */}
                <div style={{ textAlign: 'center', marginTop: '20px' }}>
                    <img
                        src={modHover ? modifyImgO : modifyImgN}
                        alt="Modify"
                        onMouseEnter={() => setModHover(true)}
                        onMouseLeave={() => setModHover(false)}
                        onClick={handleModify}
                        style={{ cursor: 'pointer', marginRight: '10px', verticalAlign: 'middle', display: rolesData.length > 0 ? 'inline' : 'none' }}
                    />
                    <img
                        src={closeHover ? closeImgO : closeImgN}
                        alt="Close"
                        onMouseEnter={() => setCloseHover(true)}
                        onMouseLeave={() => setCloseHover(false)}
                        onClick={handleClose}
                        style={{ cursor: 'pointer', verticalAlign: 'middle' }}
                    />
                </div>
            </div>
        </>
    );
};

export default EL_ManageRoles;